// src/utils/ascentTypes.js

/**
 * Comprehensive ascent type categorization and validation based on theCrag export format
 */
export const ASCENT_TYPES = {
  // Core clean ascent types shared between sport and trad
  CLEAN_LEAD: {
    ONSIGHT: 'Onsight',
    FLASH: 'Flash',
    RED_POINT: 'Red point',
    PINK_POINT: 'Pink point',
  },

  // Sport-specific clean ascents
  SPORT_SPECIFIC: {
    TOP_ROPE_CLEAN: 'Top rope clean',
    TOP_ROPE_ONSIGHT: 'Top rope onsight',
    TOP_ROPE_FLASH: 'Top rope flash',
    ROPED_SOLO: 'Roped Solo'
  },

  // Trad-specific clean ascents
  TRAD_SPECIFIC: {
    GREENPOINT: 'Greenpoint',
    GREENPOINT_ONSIGHT: 'Greenpoint onsight',
    GROUND_UP: 'Ground up red point',
    SOLO: 'Solo',
  },

  BOULDER_CLEAN: {
    SEND: 'Send',
    FLASH: 'Flash',
    ONSIGHT: 'Onsight',
    REPEAT: 'Repeat'
  },

  // First Ascent is always clean but may have taken multiple attempts
  FIRST_ASCENT: 'First ascent',

  // Failed attempts
  FAILED: {
    AID: 'Aid',
    ATTEMPT: 'Attempt',
    GHOST: 'Ghost',
    RETREAT: 'Retreat',
    HANG_DOG: 'Hang dog',
    WORKING: 'Working',
    SECOND_WITH_REST: 'Second with rest',
    TOP_ROPE_WITH_REST: 'Top rope with rest',
    ALL_FREE_WITH_REST: 'All free with rest'
  },

  // Ambiguous ascent types that need more context
  AMBIGUOUS: {
    TICK: 'Tick',
    LEAD: 'Lead',
    LEAD_SOLO: 'Lead solo',
    SECOND: 'Second'
  },

  NON_ASCENT: {
    TARGET: 'Target',
    MARK: 'Mark'
  }
};

export const ROUTE_STYLES = {
  SPORT: 'Sport',
  TRAD: 'Trad',
  BOULDER: 'Boulder',
  MIXED: 'Mixed',
  AID: 'Aid'
};

// Flatten categories and export for use in other files
const CLEAN_LEAD_ASCENTS = Object.values(ASCENT_TYPES.CLEAN_LEAD);
const SPORT_SPECIFIC_ASCENTS = Object.values(ASCENT_TYPES.SPORT_SPECIFIC);
const TRAD_SPECIFIC_ASCENTS = Object.values(ASCENT_TYPES.TRAD_SPECIFIC);
export const CLEAN_BOULDER_ASCENTS = Object.values(ASCENT_TYPES.BOULDER_CLEAN);
export const FAILED_ASCENTS = Object.values(ASCENT_TYPES.FAILED);
export const AMBIGUOUS_ASCENTS = Object.values(ASCENT_TYPES.AMBIGUOUS);
export const NON_ASCENTS = Object.values(ASCENT_TYPES.NON_ASCENT);

// Combined lists for sport and trad
export const CLEAN_SPORT_ASCENTS = [...CLEAN_LEAD_ASCENTS, ...SPORT_SPECIFIC_ASCENTS];
export const CLEAN_TRAD_ASCENTS = [...CLEAN_LEAD_ASCENTS, ...TRAD_SPECIFIC_ASCENTS];

export const FIRST_TIME_ASCENTS = [
  // Lead climbing (both sport and trad)
  ASCENT_TYPES.CLEAN_LEAD.ONSIGHT, 
  ASCENT_TYPES.CLEAN_LEAD.FLASH,
  
  // Sport specific
  ASCENT_TYPES.SPORT_SPECIFIC.TOP_ROPE_ONSIGHT,
  ASCENT_TYPES.SPORT_SPECIFIC.TOP_ROPE_FLASH,
  
  // Trad specific  
  ASCENT_TYPES.TRAD_SPECIFIC.GREENPOINT_ONSIGHT,
  
  // Boulder specific
  ASCENT_TYPES.BOULDER_CLEAN.ONSIGHT,
  ASCENT_TYPES.BOULDER_CLEAN.FLASH
];

// Sport and trad redpoint-style ascents
export const RED_OR_PINK_POINT_ASCENTS = [
  // Lead climbing (both sport and trad)
  ASCENT_TYPES.CLEAN_LEAD.RED_POINT,
  ASCENT_TYPES.CLEAN_LEAD.PINK_POINT,
  
  // Trad specific
  ASCENT_TYPES.TRAD_SPECIFIC.GROUND_UP,
  ASCENT_TYPES.TRAD_SPECIFIC.GREENPOINT,
  
  // First ascent - any style but always clean
  ASCENT_TYPES.FIRST_ASCENT
];

export const isCleanAscent = (ascentType, routeStyle) => {
  // First Ascent is always clean
  if (ascentType === ASCENT_TYPES.FIRST_ASCENT) {
    return true;
  }

  switch (routeStyle) {
    case ROUTE_STYLES.SPORT:
      // Sport routes can be climbed either sport style or trad style (greenpoint)
      return [...CLEAN_SPORT_ASCENTS, ...CLEAN_TRAD_ASCENTS].includes(ascentType);
    case ROUTE_STYLES.TRAD:
      // Trad routes only count trad ascents
      return CLEAN_TRAD_ASCENTS.includes(ascentType);
    case ROUTE_STYLES.BOULDER:
      return CLEAN_BOULDER_ASCENTS.includes(ascentType);
    case ROUTE_STYLES.MIXED:
      // Mixed routes accept either sport or trad clean ascents
      return [...CLEAN_SPORT_ASCENTS, ...CLEAN_TRAD_ASCENTS].includes(ascentType);
    default:
      return false;
  }
};

export const isFirstAscent = (ascentType) => {
  return ascentType === ASCENT_TYPES.FIRST_ASCENT;
};

export const isFirstTimeAscent = (ascentType) => {
  return FIRST_TIME_ASCENTS.includes(ascentType);
  // Removed the First Ascent check since it's not necessarily first try
};

export const isRedOrPinkPoint = (ascentType) => {
  return RED_OR_PINK_POINT_ASCENTS.includes(ascentType);
};

export const getAscentPriority = (ascentType) => {
  if (ascentType === ASCENT_TYPES.CLEAN_LEAD.ONSIGHT || 
      ascentType === ASCENT_TYPES.TRAD_SPECIFIC.GREENPOINT_ONSIGHT ||
      ascentType === ASCENT_TYPES.BOULDER_CLEAN.ONSIGHT) {
    return 0;
  }
  if (ascentType === ASCENT_TYPES.CLEAN_LEAD.FLASH ||
      ascentType === ASCENT_TYPES.BOULDER_CLEAN.FLASH) {
    return 1;
  }
  if (isRedOrPinkPoint(ascentType)) {
    return 2;  // First Ascent now has same priority as other redpoint-style ascents
  }
  if (isCleanAscent(ascentType, ROUTE_STYLES.SPORT) || 
      isCleanAscent(ascentType, ROUTE_STYLES.TRAD) || 
      isCleanAscent(ascentType, ROUTE_STYLES.BOULDER)) {
    return 3;
  }
  return 999; // Failed or ambiguous ascents
};

// Rest of the utility functions remain the same